import React, { useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { differenceInDays } from 'date-fns';

import { Box, Button, Flex } from '@chakra-ui/core';
import { Formik } from 'formik';
import { useOperatorFilter } from '../common/contexts/OperatorFilterContext';
import { PageIndex } from '../common/components/layouts/Page/PageIndex';
import {
  Table,
  Thead,
  TH,
  TD,
  THeadRow,
  TBodyRow,
  TablePlaceholder,
  TableResults,
} from '../common/components/Table';
import { expectedQuotesActions } from '../redux/expectedQuotes/actions';
import { Pagination } from '../common/components/Pagination';
import { TypeTag } from '../tasks/components';
import { getDateFromString, encodeQueryArray } from '../common/utils';

import { SearchInput } from '../common/components/Form/FormInputs';
import { SelectInput, SelectInputMultiple } from '../common/components/Form/FormSelectInput';
import { PrimaryButton, SecondaryButton } from '../common/components/Buttons';
import { usersAPI } from '../common/api/users';
import { foldersAPI } from '../common/api/folders';
import { is } from '../roleBasedAccess/Is';
import { Funders } from '../common/components/Funders/Funders';
import QuoteUpload from '../common/components/QuoteUpload/QuoteUpload';
import Comments from '../common/components/Comments/Comments';
import Can from '../roleBasedAccess/Can';
import { QuoteDocumentsAction } from '../common/components/QuoteDocumentsAction';

export const DELAYS_TYPES = {
  blue: '< 15j',
  orange: '15j << x >> 45j',
  red: '> 45j',
};

export const ExpectedQuotesIndexComponent = ({
  match,
  getExpectedQuotes,
  updateQuotesComments,
  pending,
  quotes = [],
  meta,
}) => {
  const { user } = useSelector(state => state.authentication);
  const { isFilterDisabled } = useOperatorFilter();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState({
    keywords: '',
    delay: '',
    tags: [],
  });
  const canAccessErgoReport =
    is(user, 'référent entreprise') ||
    is(user, 'coordinateur entreprises') ||
    is(user, 'administrateur');

  useEffect(() => {
    getExpectedQuotes(currentPage, search, isFilterDisabled);
  }, [getExpectedQuotes, currentPage, search, isFilterDisabled]);

  return (
    <PageIndex
      pageAction={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <Header match={match} meta={meta} onSearch={setSearch} />
      }
    >
      <Table>
        <Thead>
          <THeadRow>
            <TH size="sm">
              Nom de
              <br />
              l'entreprise
            </TH>
            <TH size="sm">
              N° de
              <br />
              dossier
            </TH>
            <TH size="sm">Bénéficiaire</TH>
            <TH size="sm">Ville</TH>
            <TH size="sm">Indicateur</TH>
            <TH size="sm">Date de demande</TH>
            <TH size="sm">
              {user.role === 'coordinateur entreprises' ? 'Tag' : 'Type de dossier'}
            </TH>
            <TH size="sm">Actions</TH>
          </THeadRow>
        </Thead>
        <tbody>
          {!quotes || pending ? (
            <TablePlaceholder colSpan={8} />
          ) : (
            quotes.map((quote, index) => {
              const {
                id,
                quoteGroupId,
                companyId,
                companyName,
                folderFormattedId,
                recipientFullName,
                recipientLastName,
                recipientFirstName,
                folderId,
                folderState,
                comments,
                recipientHousingCity,
                quoteRequestSentAt,
                lastQuoteUploaded,
                ergoReport,
                folderTag,
                indicator,
                typeOfVisit,
                quoteVersion,
              } = quote;

              const nbOfDaysDelayQuoteRequestSentAt = quoteRequestSentAt
                ? differenceInDays(
                    new Date().setHours(0, 0, 0, 0),
                    getDateFromString(quoteRequestSentAt),
                  )
                : null;

              let variantColor = 'blue';
              let folderType = 'Devis après rapport ergo';

              if (
                nbOfDaysDelayQuoteRequestSentAt > 45 &&
                indicator?.match(
                  /(Devis non reçu|Devis reçu non analysé|Devis reçu non analysé\*|Devis reçu en cours d’analyse|Devis reçu en cours d’analyse\*|Devis conforme en attente de transmission au bénéficiaire|Demande de modifications)/,
                )
              ) {
                variantColor = 'red';
              }

              if (
                nbOfDaysDelayQuoteRequestSentAt > 15 &&
                nbOfDaysDelayQuoteRequestSentAt <= 45 &&
                indicator?.match(
                  /(Devis non reçu|Devis reçu non analysé|Devis reçu non analysé\*|Devis reçu en cours d’analyse|Devis reçu en cours d’analyse\*|Devis conforme en attente de transmission au bénéficiaire|Demande de modifications)/,
                )
              ) {
                variantColor = 'orange';
              }

              if (typeOfVisit === 'joint') {
                folderType = 'VAD conjoint';
              }
              if (typeOfVisit === 'existing_quote') {
                folderType = 'Intervention sur devis';
              }

              return (
                // eslint-disable-next-line react/no-array-index-key
                <TBodyRow key={index} hasScaleOnHover={false} enableHover={false}>
                  <TD hasPadding size="sm">
                    {companyName}
                  </TD>
                  <TD hasPadding size="sm">
                    {folderFormattedId}
                  </TD>
                  <TD hasPadding size="sm">
                    {recipientFullName}
                  </TD>
                  <TD hasPadding size="sm">
                    {recipientHousingCity}
                  </TD>
                  <TD hasPadding size="sm">
                    {quoteVersion}
                  </TD>
                  <TD hasPadding size="sm">
                    {nbOfDaysDelayQuoteRequestSentAt === null ||
                    nbOfDaysDelayQuoteRequestSentAt < 0 ? (
                      'En attente'
                    ) : (
                      <TypeTag variantColor={variantColor} whiteSpace="nowrap">
                        {quoteRequestSentAt} (+{nbOfDaysDelayQuoteRequestSentAt}j)
                      </TypeTag>
                    )}
                  </TD>
                  <TD
                    hasPadding
                    size="sm"
                    textTransform={user.role === 'coordinateur entreprises' ? 'uppercase' : null}
                  >
                    {user.role === 'coordinateur entreprises' ? folderTag : folderType}
                  </TD>
                  <TD textAlign="center" whiteSpace="nowrap">
                    <Funders
                      folderId={folderId}
                      quote={quote}
                      trigger={({ onAskQuestion }) =>
                        onAskQuestion ? (
                          <Button
                            type="button"
                            variant="link"
                            variantColor="neutral"
                            size="xs"
                            color="coral.700"
                            display="block"
                            mx="auto"
                            mb={2}
                            onClick={onAskQuestion}
                          >
                            Poser une question
                          </Button>
                        ) : null
                      }
                    />
                    <QuoteUpload
                      folder={{ formattedId: folderFormattedId, recipientFullName }}
                      quote={quote}
                      quoteRequest={{ id, quoteGroupId, companyId, companyName, indicator }}
                      successCallback={() => getExpectedQuotes(currentPage, search)}
                    />

                    <QuoteDocumentsAction
                      lastQuoteUploaded={lastQuoteUploaded}
                      ergoReport={canAccessErgoReport ? ergoReport : null}
                    />

                    <Can
                      perform="comments:read"
                      yes={() => (
                        <Comments
                          folder={{
                            formattedId: folderFormattedId,
                            id: folderId,
                            state: folderState,
                            recipientFirstName,
                            recipientLastName,
                          }}
                          comments={comments}
                          updateFallback={updatedFolder => updateQuotesComments(id, updatedFolder)}
                        />
                      )}
                    />
                  </TD>
                </TBodyRow>
              );
            })
          )}
        </tbody>
      </Table>
      {meta && (
        <Pagination
          current={currentPage}
          total={meta.total}
          defaultPageSize={meta.perPage}
          onChange={page => setCurrentPage(page)}
        />
      )}
    </PageIndex>
  );
};

const mapStateToProps = ({ expectedQuotes: state }) => {
  return {
    pending: state.pending,
    quotes: state.data,
    meta: state.meta,
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getExpectedQuotes: expectedQuotesActions.getQuotes,
      updateQuotesComments: expectedQuotesActions.updateQuotesComments,
    },
    dispatch,
  );

export const ExpectedQuotesIndex = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ExpectedQuotesIndexComponent);

const SearchBar = ({ onSearch }) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [currentTags, setCurrentTags] = useState([]);
  const [currentIndicators, setCurrentIndicators] = useState([]);
  const { user } = useSelector(state => state.authentication);

  const form = useRef();

  useEffect(() => {
    if (is(user, 'pilote externe') || is(user, 'référent externe'))
      usersAPI
        .getUser(-1)
        .then(u => {
          setAvailableTags(u.tags);
        })
        .catch(console.log);
    else
      foldersAPI
        .getTags({ search: '' })
        .then(tags => {
          setAvailableTags(tags);
        })
        .catch(console.log);
  }, [setAvailableTags, user]);

  return (
    <Flex isInline justifyContent="space-between" w="100%">
      <Formik
        innerRef={form}
        initialValues={{ search: '', delay: '' }}
        onSubmit={values =>
          onSearch({
            keywords: values.search,
            delay: values.delay?.value || values.delay,
            tags: currentTags,
          })
        }
      >
        {formik => (
          <Box
            as="form"
            onSubmit={formik.handleSubmit}
            d="flex"
            alignItems="start"
            flexGrow="1"
            minW={0}
            mr={3}
          >
            <Box d="flex" flexDir="column" width="100%" flexGrow={1} minW={0}>
              <SearchInput name="search" />
              <Box d="flex" alignItems="start" flexWrap="wrap" mt={2}>
                <SelectInputMultiple
                  placeholder="Filtrer par TAGS"
                  styles={{ flexGrow: 1, maxW: '24%', mr: 3, textTransform: 'uppercase' }}
                  options={availableTags}
                  menuOptionGroupOptions={{
                    onChange: setCurrentTags,
                    value: currentTags,
                    clear: () => {
                      setCurrentTags([]);
                    },
                  }}
                  label="Tag"
                />
                <SelectInput
                  name="delay"
                  isClearable
                  placeholder="Délais"
                  styles={{
                    flexGrow: 1,
                    maxW: '24%',
                    mr: 3,
                  }}
                  css={{
                    '[class*=-indicatorContainer]:first-of-type': {
                      position: 'static',
                    },
                  }}
                  size="sm"
                  options={[
                    { label: '< 15j', value: '< 15' },
                    { label: '15j < x < 45j', value: '15 < x < 45' },
                    { label: '> 45j', value: '> 45' },
                  ]}
                />
              </Box>
            </Box>
            <SecondaryButton type="submit" size="sm" ml={3}>
              Lancer la recherche
            </SecondaryButton>
          </Box>
        )}
      </Formik>
    </Flex>
  );
};

const Header = ({ onSearch, meta }) => {
  return (
    <>
      <SearchBar onSearch={onSearch} meta={meta} />
      <TableResults ressource="devis" style={{ mt: -3 }} total={meta ? meta.total : 0} />
    </>
  );
};
